<template>
  <div :id="`filter-container-${props.filterid}`" class="filter-container">
    <div v-if="props.filterid === 'last_seen_seconds' || props.filterid === 'fromto'" class="datepicker-wrapper">
      <DatePicker
        id="filter-last_seen_seconds"
        v-model="dateFilter"
        class="filter-date"
        format="DD/MM/YYYY HH:mm"
        :placeholder="props.filterid === 'fromto' ? $t('device.filter.ids.fromto') : $t('device.filter.ids.last_seen_seconds')"
        :defaultPickerValue="getToday()"
        :showTime="{ format: 'HH:mm' }"
        :showToday="false"
        @ok="updateFilter"
        @change="handleChange"
      />
      <v-icon class="menu-down-icon">mdi-menu-down</v-icon>
    </div>
    <v-menu
      v-else
      attach
      bottom
      offset-y
      :close-on-content-click="false"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          :id="`open-filter-${props.filterid}`"
          dark
          v-bind="attrs"
          :disabled="(props.filterid === 'state' || props.filterid === 'command' || props.filterid === 'event') && !props.suggestions.name"
          v-on="on"
        >
          <span class="btn-title">{{ $t(`device.list.item.${props.translatekey}`) }}</span>
          <div v-if="filterCount" class="numberCircle">{{ filterCount }}</div>
          <v-icon dark small>mdi-menu-down</v-icon>
        </v-btn>
      </template>
      <TreeMenu
        v-if="(props.filterid === 'state' || props.filterid === 'command' || props.filterid === 'event')"
        :children="props.suggestions.children"
        :name="props.suggestions.name"
        :parentCategory="props.filterid"
        :class="'top-menu'"
        @eventBusAddCat="eventBusAddCat"
        @eventBusDelCat="eventBusDelCat"
      />
      <v-text-field
        v-else-if="props.filterid === 'dtco_ids'"
        id="filter-dtco-ids"
        v-model="dtcoIds"
        :label="$t('device.filter.search_dtco_ids')"
        single-line
        outlined
        dark
        dense
        hide-details
        :color="colors.secondary"
        class="search-dtco-input"
        @keyup.enter.native="updateFilter"
      />
      <v-list v-else class="scrollable-list">
        <v-list-item
          v-for="(suggestion, index) in getSuggestions"
          :id="`filter-${props.filterid}`"
          :key="index"
        >
          <v-checkbox
            v-if="props.filterid === 'variant_ids'"
            id="filter-value-variant_ids"
            v-model="filter"
            dark
            :value="suggestion.id"
            :label="suggestion.name"
            on-icon="mdi-checkbox-intermediate"
            :color="colors.secondary"
            hide-details="true"
            @change="updateFilter"
          />
          <v-checkbox
            v-else
            :id="`filter-value-${props.filterid}-${index}`"
            v-model="filter"
            dark
            :value="suggestion"
            :label="suggestion"
            on-icon="mdi-checkbox-intermediate"
            :color="colors.secondary"
            hide-details="true"
            @change="updateFilter"
          />
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import moment, { Moment } from 'moment';
import { DatePicker } from 'ant-design-vue';
import TreeMenu from '@/components/common/Tree/TreeMenu.vue';
import { colors } from '@/constants/theme/colors';
import './styles/filter-item.scss';

const emit = defineEmits(['sendFilter', 'addHistoryCat', 'delHistoryCat']);

const props = defineProps({
  filterid: {
    type: String,
    default: ''
  },
  translatekey: {
    type: String,
    default: ''
  },
  suggestions: {
    type: Object as any,
    default: [] as any[]
  },
  reset: Boolean,
  resetMe: {
    type: String,
    default: ''
  },
  appliedFilters: {
    type: Array,
    default: [] as []
  }
});

const getSuggestions = computed(() => props.suggestions[props.filterid]);

const filter = ref([]);
const dateFilter = ref<Moment | null>(null);

const filterCount = ref(0);

const dtcoIds = ref('');

function updateFilter() {
  if (props.filterid === 'last_seen_seconds') {
    const dateChosen = moment(dateFilter.value);
    const seconds = moment().diff(dateChosen, 'seconds');
    emit('sendFilter', props.filterid, seconds);
    filterCount.value = 1;
  } else if (props.filterid === 'dtco_ids') {
    const ids = dtcoIds.value.replace(/ /g, '').split(',');
    emit('sendFilter', props.filterid, ids);
    filterCount.value = ids.length;
  } else if (props.filterid === 'fromto') {
    const m = moment.utc({
      year: dateFilter.value?.year(),
      month: dateFilter.value?.month(),
      day: dateFilter.value?.date(),
      hour: dateFilter.value?.hour(),
      minute: dateFilter.value?.minute(),
      second: 0
    });
    emit('sendFilter', props.filterid, m.unix());
    filterCount.value = 1;
  } else {
    emit('sendFilter', props.filterid, filter.value);
    filterCount.value = filter.value.length;
  }
}

function getToday() {
  return moment().startOf('day');
}

const handleChange = (value: any) => {
  dateFilter.value = value;
  updateFilter();
};

function eventBusAddCat(cat: string) {
  emit('addHistoryCat', cat);
}

function eventBusDelCat(cat: string) {
  emit('delHistoryCat', cat);
}

// Reset specific filter
watch(
  () => props.reset,
  () => {
    filterCount.value = 0;
    filter.value = [];
    dateFilter.value = null;
  }
);

// Conditionnal filter reset
watch(() => props.resetMe, (newValue) => {
  if (newValue === props.filterid) {
    filterCount.value = 0;
    filter.value = [];
    dateFilter.value = null;
  }
});

// Update filter item on bookmark or saved filter load
watch(() => props.appliedFilters, (newFilters) => {
  (newFilters as Record<string, any>[]).forEach((f) => {
    if (props.filterid in f && props.filterid !== 'last_seen_seconds' && props.filterid !== 'fromto') {
      filter.value = f[props.filterid];
      filterCount.value = f[props.filterid].length;
    } else if (props.filterid in f && (props.filterid === 'last_seen_seconds' || props.filterid === 'fromto')) {
      dateFilter.value = null;
      filterCount.value = f[props.filterid].length;
    }
  });
});
</script>
